import { businessRoutes, locationRoutes, type Layer } from './route-map';
import { engineName, ENGINE_ORDER } from './engine-names';
import { TERMS } from '@pageboss/shared';
import type { EffectiveTier } from './types';

export interface NavChild {
  label: string;
  to: string;
  locked?: boolean;
  lockReason?: 'plan' | 'domain';
  nested?: NavChild[];
  badge?: number;
}

export interface NavGroup {
  key: Layer | 'reports';
  label: string;
  children: NavChild[];
}

export interface NavInput {
  /** Canonical URLs need both ids; the nav builds them, so it takes both. */
  businessId: string;
  siteId: string;
  tier: EffectiveTier;
  domainActive: boolean;
  trackedEngines: string[];
  unreadInbox: number;
  isOperator: boolean;
}

// ---------------------------------------------------------------------
// Active-state rules for the sidebar. Kept here, next to the shape they
// describe, so panel-shell.tsx renders the answer instead of deriving it —
// and so the rules are testable without mounting the shell.
// ---------------------------------------------------------------------

// Does a path belong to this nav entry? Prefix match, EXCEPT for an "index
// child" — a nested entry pointing at its own parent's path (e.g. "Profile"
// under "Google Profile"). That one matches exactly, or it would light up on
// every sibling route below it.
export function isNavPathActive(to: string, pathname: string, parentTo?: string): boolean {
  const isIndexChild = parentTo !== undefined && to === parentTo;
  if (isIndexChild) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

// Which entry gets the active pill: the LEAF only. A parent whose child owns
// the current path is on the way there, not where you are — highlighting both
// says "you are in two places", and on Google Profile it drew the pill on the
// parent and the child at the same time on every one of its three routes.
// (Design audit T7. The nesting is one level deep today; the recursion below
// costs nothing and keeps the rule true if that ever changes.)
export function isNavItemActive(item: NavChild, pathname: string, parentTo?: string): boolean {
  if (!isNavPathActive(item.to, pathname, parentTo)) return false;
  const childActive = item.nested?.some((child) => isNavItemActive(child, pathname, item.to));
  return childActive !== true;
}

export function buildNav(input: NavInput): { groups: NavGroup[]; footer: NavChild[] } {
  const { businessId } = input;
  const { siteId, tier, domainActive, trackedEngines, unreadInbox, isOperator } = input;

  const groups: NavGroup[] = [
    // Foundation layer
    {
      key: 'foundation',
      label: 'Foundation',
      children: [
        {
          label: 'Business info',
          to: locationRoutes.businessInfo(businessId, siteId),
        },
        {
          label: 'Website',
          to: locationRoutes.website(businessId, siteId),
          nested: [
            {
              label: 'Domain',
              to: locationRoutes.websiteDomain(businessId, siteId),
            },
          ],
        },
        ...(domainActive
          ? [
              {
                label: 'Mailboxes',
                to: businessRoutes.mailboxes(businessId),
              },
            ]
          : [
              {
                label: 'Mailboxes',
                // WP3b (D4): the lock target is the guided domain & mailbox
                // wizard, not the raw domain panel — was routes.websiteDomain.
                to: locationRoutes.domainWizard(businessId, siteId),
                locked: true as const,
                lockReason: 'domain' as const,
              },
            ]),
        {
          label: 'Get-found-ready settings',
          to: locationRoutes.getFoundReady(businessId, siteId),
        },
      ],
    },

    // Visibility layer
    {
      key: 'visibility',
      label: 'Visibility',
      children: [
        {
          label: TERMS.googleProfile,
          to: locationRoutes.googleProfile(businessId, siteId),
          nested: [
            {
              label: 'Profile',
              to: locationRoutes.googleProfile(businessId, siteId),
            },
            {
              label: 'Content',
              to: locationRoutes.googleProfileContent(businessId, siteId),
            },
            {
              label: 'Map',
              to: locationRoutes.googleProfileMap(businessId, siteId),
            },
          ],
        },
        {
          label: 'AI visibility',
          to: locationRoutes.aiVisibility(businessId, siteId),
          nested: [...trackedEngines]
            .sort((a, b) => {
              const aIndex = ENGINE_ORDER.indexOf(a);
              const bIndex = ENGINE_ORDER.indexOf(b);
              return (aIndex !== -1 ? aIndex : Infinity) - (bIndex !== -1 ? bIndex : Infinity);
            })
            .map((engine) => ({
              label: engineName(engine),
              to: locationRoutes.aiVisibility(businessId, siteId, engine),
            })),
        },
        // Search Console (Phase 1). Sits between AI visibility and the AEO
        // list on purpose: it is the last "what does an engine say about
        // me" measurement, and the AEO list below it is where the un-indexed
        // pages it finds turn into work. Deliberately never `locked` — the
        // tier gate lives on the screen (the API's `locked` state), and a
        // padlock here would hide a page a downgraded org still needs in
        // order to disconnect Google.
        {
          label: 'Google Search',
          to: locationRoutes.search(businessId, siteId),
        },
        {
          label: 'AEO audit & tasks',
          to: locationRoutes.aeo(businessId, siteId),
        },
      ],
    },

    // Reputation layer
    {
      key: 'reputation',
      label: 'Reputation',
      children: [
        {
          label: 'Reviews & replies',
          to: locationRoutes.reviews(businessId, siteId),
        },
        {
          label: 'Reputation settings',
          to: locationRoutes.reputationSettings(businessId, siteId),
        },
      ],
    },

    // Pulse layer
    {
      key: 'pulse',
      label: 'Pulse',
      children: [
        {
          label: 'Inbox',
          to: locationRoutes.inbox(businessId, siteId),
          badge: unreadInbox,
        },
        {
          label: 'Leads',
          to: locationRoutes.leads(businessId, siteId),
        },
        {
          label: 'Analytics',
          to: locationRoutes.analytics(businessId, siteId),
        },
        {
          label: 'Surveys',
          to: businessRoutes.surveys(businessId),
        },
      ],
    },

    // Reports
    {
      key: 'reports',
      label: 'Reports',
      children: [
        {
          label: 'All reports',
          to: locationRoutes.reports(businessId, siteId),
        },
      ],
    },
  ];

  const footer: NavChild[] = [
    ...(tier === 'growth'
      ? [
          {
            label: 'Locations',
            to: businessRoutes.locations(businessId),
          },
        ]
      : [
          {
            label: 'Locations',
            to: businessRoutes.locations(businessId),
            locked: true as const,
            lockReason: 'plan' as const,
          },
        ]),
    {
      label: 'Team',
      to: businessRoutes.team(businessId),
    },
    {
      label: 'Billing',
      to: businessRoutes.billing(businessId),
    },
    {
      label: 'Settings',
      to: businessRoutes.settings(businessId),
    },
  ];

  if (isOperator) {
    footer.push({
      label: 'Console',
      to: 'https://manage.pageboss.com',
    });
  }

  return { groups, footer };
}
